import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  createSeasonPassProduct,
  deleteSeasonPassProduct,
  getAllSeasonPassProducts,
  getParkingAreasForSelect,
  getActiveCpmsConnections,
  updateSeasonPassProduct,
  updateSeasonPassProductAvailability,
} from "./seasonPassProductsAPI";

const errorMessage = (error) =>
  error.response?.data?.detail ??
  error.response?.data?.message ??
  error.response?.data?.error ??
  error.message;

export const seasonPassProductsSlice = createSlice({
  name: "seasonPassProducts",
  initialState: {
    seasonPassProducts: [],
    parkingAreas: [],
    cpmsConnections: [],
    cpmsConnectionsError: "",
    parkingAreaFilter: "",
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "createdAt",
    selectedId: null,
    selectedProduct: null,
    addModalOpen: false,
    editModalOpen: false,
    deleteModalOpen: false,
    successMessage: "",
    errorMessage: "",
    listStatus: "idle",
    listError: "",
  },
  reducers: {
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    setParkingAreaFilter: (state, action) => {
      state.parkingAreaFilter = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSeasonPassProducts: (state, action) => {
      state.seasonPassProducts = action.payload;
    },
    setParkingAreas: (state, action) => {
      state.parkingAreas = action.payload;
    },
    setCpmsConnections: (state, action) => {
      state.cpmsConnections = action.payload;
    },
    setCpmsConnectionsError: (state, action) => {
      state.cpmsConnectionsError = action.payload;
    },
    setListStatus: (state, action) => {
      state.listStatus = action.payload;
    },
    setListError: (state, action) => {
      state.listError = action.payload;
    },
    showAddModal: (state, action) => {
      state.addModalOpen = action.payload;
    },
    showEditModal: (state, action) => {
      state.editModalOpen = action.payload;
    },
    showDeleteModal: (state, action) => {
      state.deleteModalOpen = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setSelectedId,
  setSelectedProduct,
  setParkingAreaFilter,
  setPage,
  setRowsPerPage,
  setPagination,
  setSort,
  setSortBy,
  setSeasonPassProducts,
  setParkingAreas,
  setCpmsConnections,
  setCpmsConnectionsError,
  setListStatus,
  setListError,
  showAddModal,
  showEditModal,
  showDeleteModal,
  setSuccessMessage,
  setErrorMessage,
} = seasonPassProductsSlice.actions;

export const selectState = (state) => state.seasonPassProducts;

export const handleGetSeasonPassProducts = () => async (dispatch, getState) => {
  dispatch(showLoading());
  dispatch(setListStatus("loading"));
  dispatch(setListError(""));
  const state = getState();

  try {
    const response = await getAllSeasonPassProducts(
      state.seasonPassProducts.page,
      state.seasonPassProducts.rowsPerPage,
      state.seasonPassProducts.sort,
      state.seasonPassProducts.sortBy,
      state.seasonPassProducts.parkingAreaFilter
    );
    const { content, ...pagination } = response.data;

    dispatch(setPagination(pagination));
    dispatch(setSeasonPassProducts(content));
    dispatch(setSelectedId(null));
    dispatch(setSelectedProduct(null));
    dispatch(setListStatus("succeeded"));
  } catch (error) {
    dispatch(setListError(errorMessage(error)));
    dispatch(setListStatus("failed"));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleGetCpmsConnections = () => async (dispatch) => {
  try {
    const response = await getActiveCpmsConnections();
    dispatch(setCpmsConnections(response.data.filter(({ status }) => status === "ACTIVE")));
    dispatch(setCpmsConnectionsError(""));
  } catch (error) {
    dispatch(setCpmsConnections([]));
    dispatch(setCpmsConnectionsError(
      error.response?.status === 403
        ? "You need the admin role to view available CPMS connections."
        : errorMessage(error)
    ));
  }
};

export const handleGetParkingAreas = () => async (dispatch) => {
  try {
    const response = await getParkingAreasForSelect();
    dispatch(setParkingAreas(response.data.content ?? []));
  } catch (error) {
    console.error(error.message);
  }
};

export const handleAddSeasonPassProduct =
  (product) => async (dispatch) => {
    dispatch(showLoading());

    try {
      await createSeasonPassProduct(product);
      dispatch(showAddModal(false));
      dispatch(setSuccessMessage("Successfully added season pass plan!"));
      dispatch(handleGetSeasonPassProducts());
    } catch (error) {
      dispatch(
        setErrorMessage(errorMessage(error))
      );
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleUpdateSeasonPassProduct =
  (product) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      await updateSeasonPassProduct(
        state.seasonPassProducts.selectedId,
        product
      );
      dispatch(showEditModal(false));
      dispatch(setSuccessMessage("Successfully updated season pass plan!"));
      dispatch(handleGetSeasonPassProducts());
    } catch (error) {
      dispatch(
        setErrorMessage(errorMessage(error))
      );
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleUpdateSeasonPassProductAvailability =
  (isActive) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      await updateSeasonPassProductAvailability(
        state.seasonPassProducts.selectedId,
        isActive
      );
      dispatch(
        setSuccessMessage(
          `Successfully ${isActive ? "activated" : "deactivated"} season pass plan!`
        )
      );
      dispatch(handleGetSeasonPassProducts());
    } catch (error) {
      dispatch(setErrorMessage(errorMessage(error)));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleDeleteSeasonPassProduct =
  () => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      await deleteSeasonPassProduct(state.seasonPassProducts.selectedId);
      dispatch(showDeleteModal(false));
      dispatch(setSuccessMessage("Successfully deleted season pass plan!"));
      dispatch(handleGetSeasonPassProducts());
    } catch (error) {
      dispatch(
        setErrorMessage(
          error.response?.status === 409
            ? "This plan cannot be deleted because a season pass references it. Deactivate it instead."
            : errorMessage(error)
        )
      );
    } finally {
      dispatch(hideLoading());
    }
  };

export default seasonPassProductsSlice.reducer;
